import { LayerConfig } from '../../../types/metadata';
import { type ColumnFilter } from '../utils/tableFilterUtils';

const LAYER_STATE_STORAGE_KEY = 'map-layer-state';
const LAYER_FILTERS_STORAGE_KEY = 'map-layer-filters';
const DEBOUNCE_MS = 1000;

/**
 * Shape of the persisted layer state.
 * We only store configs and order — NOT the heavy data arrays.
 */
export interface PersistedLayerState {
  layerIds: string[];
  layersConfig: Record<string, LayerConfig>;
  savedAt: number; // Timestamp for staleness detection
}

let debounceTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Save layer state to localStorage with debouncing.
 * Debounces rapid changes (e.g., opacity slider) to avoid thrashing.
 */
export const saveLayerState = (layerIds: string[], layersConfig: Record<string, LayerConfig>): void => {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }

  debounceTimer = setTimeout(() => {
    try {
      const state: PersistedLayerState = {
        layerIds,
        layersConfig,
        savedAt: Date.now(),
      };
      localStorage.setItem(LAYER_STATE_STORAGE_KEY, JSON.stringify(state));
      console.log('[LayerPersistence] ✅ Saved layer state', {
        layers: layerIds.length,
        names: layerIds.map(id => layersConfig[id]?.name).filter(Boolean),
      });
    } catch (error) {
      console.error('[LayerPersistence] ❌ Failed to save layer state:', error);
    }
  }, DEBOUNCE_MS);
};

/**
 * Immediately save layer state (no debounce).
 * Used for critical operations like remove/add where you want immediate persistence.
 */
export const saveLayerStateImmediate = (layerIds: string[], layersConfig: Record<string, LayerConfig>): void => {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }

  try {
    const state: PersistedLayerState = {
      layerIds,
      layersConfig,
      savedAt: Date.now(),
    };
    localStorage.setItem(LAYER_STATE_STORAGE_KEY, JSON.stringify(state));
    console.log('[LayerPersistence] ✅ Saved layer state (immediate)', {
      layers: layerIds.length,
    });
  } catch (error) {
    console.error('[LayerPersistence] ❌ Failed to save layer state:', error);
  }
};

/**
 * Load persisted layer state from localStorage.
 * Returns null if nothing is saved or data is corrupt.
 */
export const loadLayerState = (): PersistedLayerState | null => {
  try {
    const stored = localStorage.getItem(LAYER_STATE_STORAGE_KEY);
    if (!stored) return null;

    const state: PersistedLayerState = JSON.parse(stored);

    // Basic validation
    if (!Array.isArray(state.layerIds) || typeof state.layersConfig !== 'object') {
      console.warn('[LayerPersistence] ⚠️ Invalid persisted state, ignoring');
      return null;
    }

    console.log('[LayerPersistence] 📂 Loaded persisted layer state', {
      layers: state.layerIds.length,
      savedAt: new Date(state.savedAt).toLocaleString(),
      names: state.layerIds.map(id => state.layersConfig[id]?.name).filter(Boolean),
    });

    return state;
  } catch (error) {
    console.error('[LayerPersistence] ❌ Failed to load layer state:', error);
    return null;
  }
};

/**
 * Clear persisted layer state.
 */
export const clearLayerState = (): void => {
  localStorage.removeItem(LAYER_STATE_STORAGE_KEY);
  console.log('[LayerPersistence] 🗑️ Cleared persisted layer state');
};

/**
 * Save layer filters to localStorage.
 */
export const saveLayerFilters = (layerFilters: Record<string, ColumnFilter[]>): void => {
  try {
    // Only persist non-empty filters
    const toSave: Record<string, ColumnFilter[]> = {};
    for (const [id, filters] of Object.entries(layerFilters)) {
      if (filters.length > 0) {
        toSave[id] = filters;
      }
    }
    localStorage.setItem(LAYER_FILTERS_STORAGE_KEY, JSON.stringify(toSave));
  } catch (error) {
    console.error('[LayerPersistence] ❌ Failed to save layer filters:', error);
  }
};

/**
 * Load persisted layer filters from localStorage.
 */
export const loadLayerFilters = (): Record<string, ColumnFilter[]> => {
  try {
    const stored = localStorage.getItem(LAYER_FILTERS_STORAGE_KEY);
    if (!stored) return {};
    const filters = JSON.parse(stored);
    if (typeof filters !== 'object') return {};
    console.log('[LayerPersistence] 📂 Loaded persisted layer filters', {
      layers: Object.keys(filters).length,
    });
    return filters;
  } catch (error) {
    console.error('[LayerPersistence] ❌ Failed to load layer filters:', error);
    return {};
  }
};
